import { getApiUrl, getAuthToken } from './endpoints';
import type {
  IntegrationAccessPolicy,
  IntegrationConnectionTestResult,
  IntegrationEndpoint,
  IntegrationProject,
  IntegrationProjectInput,
} from '../types/integration';

function getHeaders(json = false): Record<string, string> {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getApiUrl()}${path}`, init);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Integration request failed: HTTP ${response.status}`);
  }
  return payload as T;
}

export const integrationService = {
  async listProjects(): Promise<IntegrationProject[]> {
    const result = await request<{ projects: IntegrationProject[] }>('/api/integrations', {
      headers: getHeaders(),
    });
    return result.projects;
  },

  async getProject(id: string): Promise<{
    project: IntegrationProject;
    endpoints: IntegrationEndpoint[];
  }> {
    return request(`/api/integrations/${encodeURIComponent(id)}`, {
      headers: getHeaders(),
    });
  },

  async createProject(input: IntegrationProjectInput): Promise<IntegrationProject> {
    const result = await request<{ project: IntegrationProject }>('/api/integrations', {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(input),
    });
    return result.project;
  },

  async updateProject(
    id: string,
    input: Partial<IntegrationProjectInput>,
  ): Promise<IntegrationProject> {
    const result = await request<{ project: IntegrationProject }>(
      `/api/integrations/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify(input),
      },
    );
    return result.project;
  },

  async deleteProject(id: string): Promise<void> {
    await request(`/api/integrations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
  },

  async testConnection(id: string): Promise<IntegrationConnectionTestResult> {
    return request(`/api/integrations/${encodeURIComponent(id)}/test`, {
      method: 'POST',
      headers: getHeaders(true),
      body: '{}',
    });
  },

  async discover(id: string): Promise<IntegrationEndpoint[]> {
    const result = await request<{ endpoints: IntegrationEndpoint[] }>(
      `/api/integrations/${encodeURIComponent(id)}/discover`,
      {
        method: 'POST',
        headers: getHeaders(true),
        body: '{}',
      },
    );
    return result.endpoints;
  },

  async updateEndpoint(
    projectId: string,
    endpointId: string,
    input: { enabled?: boolean; accessPolicy?: IntegrationAccessPolicy },
  ): Promise<IntegrationEndpoint> {
    const result = await request<{ endpoint: IntegrationEndpoint }>(
      `/api/integrations/${encodeURIComponent(projectId)}/endpoints/${encodeURIComponent(endpointId)}`,
      {
        method: 'PATCH',
        headers: getHeaders(true),
        body: JSON.stringify(input),
      },
    );
    return result.endpoint;
  },
};
